import styled from 'styled-components';

/*
 * Only styles that Blade `Box`/`Text` props cannot express live here:
 * custom interactive elements (buttons/inputs/labels with hover, focus and
 * disabled styling), pseudo-elements/selectors, sibling selectors, CSS
 * transitions, negative margins and Blade-internal overrides.
 * Pure layout/spacing/colour containers use Blade `Box` directly in the JSX.
 *
 * Every pixel value below is either a Blade theme token (spacing / border /
 * typography) or a named constant defined here. Named constants are used only
 * where no token maps to the value: icon/asset geometry, connector-line
 * geometry, component-specific min sizes, listbox/menu max-heights, media
 * breakpoints, viewport calc offsets, Blade-Table/trigger alignment and the
 * decorative preview dot-grid. Each constant carries a one-line justification.
 */

// --- Step-rail icon & active dot geometry (no spacing token maps to these) ---
const STEP_ICON_SIZE = '28px'; // step-rail icon diameter — off the spacing scale
const STEP_ICON_ACTIVE_DOT = '10px'; // active-state inner dot — icon-internal geometry

// --- Dotted connector line between steps (derived from the step-rail icon) ---
const CONNECTOR_LINE_OFFSET_LEFT = '13px'; // horizontal centre of the 28px icon
const CONNECTOR_LINE_OFFSET_TOP = '30px'; // starts just below the icon
const CONNECTOR_LINE_WIDTH = '2px'; // dotted line thickness — connector geometry
const CONNECTOR_LINE_HEIGHT = '18px'; // gap spanned to the next step

// --- Component-specific min sizes (not on the spacing scale) ---
const FIELD_TYPE_BUTTON_MIN_HEIGHT = '36px'; // custom select trigger height
const FIELD_BEHAVIOR_ICON_COLUMN = '44px'; // fixed icon column in the behaviour grid
const FIELD_BEHAVIOR_ROW_MIN_HEIGHT = '82px'; // behaviour row min height
const CHIP_MIN_HEIGHT = '28px'; // enum-value chip min height
const FIXED_FOOTER_MIN_HEIGHT = '68px'; // sticky footer min height

// --- Menu / min-width constraints (not on the spacing scale) ---
const FIELD_TYPE_MENU_MAX_HEIGHT = '392px'; // listbox overlay max height before scroll
const ENUM_VALUE_INPUT_MIN_WIDTH = '120px'; // min width the chip input keeps before wrapping
const PERMISSION_ACCESS_COLUMN_WIDTH = '210px'; // fixed access-control column width
const SEARCH_EXPANDED_WIDTH = '324px'; // expanded width of the search control
const SEARCH_COLLAPSED_WIDTH = '44px'; // collapsed (icon-only) width of the search control

// --- Blade-Table header override & search-trigger alignment ---
const TABLE_HEADER_CELL_HEIGHT = '48px'; // matches Blade's own header-cell geometry
const SEARCH_TRIGGER_OFFSET_TOP = '-2px'; // aligns trigger with the sibling toolbar button
const SEARCH_TRIGGER_OFFSET_RIGHT = '10px'; // horizontal gap from the sibling toolbar button
const SEARCH_INNER_INSET = '2px'; // inner inset that centres the trigger/input within the control

// --- Media breakpoint & viewport calc offsets ---
const MOBILE_BREAKPOINT = '768px'; // layout breakpoint — not a spacing value
const TAB_LAYOUT_MIN_HEIGHT = '520px'; // minimum tab-layout height floor
const TAB_LAYOUT_VIEWPORT_OFFSET = '220px'; // chrome height subtracted from 100vh

// --- Decorative preview dot-grid background ---
const PREVIEW_DOT_RADIUS = '1px'; // radial-gradient dot radius — decorative geometry
const PREVIEW_DOT_GRID_SIZE = '14px'; // dot-grid tile size — decorative geometry

// Kept: styles a native radio input (accent-color) inside a clickable label.
export const RadioOption = styled.label(
  ({ theme }) => `
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]}px;
  cursor: pointer;

  input {
    margin-top: ${theme.spacing[1]}px;
    accent-color: ${theme.colors.interactive.icon.primary.normal};
  }
`,
);

// Kept: borderless inline text-button (font inheritance + cursor) that Blade Button/Link cannot render.
export const InlineActionButton = styled.button<{ color?: 'primary' | 'negative' }>(
  ({ theme, color = 'primary' }) => `
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]}px;
  width: fit-content;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${
    color === 'negative'
      ? theme.colors.feedback.text.negative.intense
      : theme.colors.interactive.text.primary.normal
  };
  cursor: pointer;
  font: inherit;
  font-weight: 600;
`,
);

// Kept: dotted connector line between steps needs a ::after pseudo-element.
export const StepRailItem = styled.div(
  ({ theme }) => `
  position: relative;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]}px;
  margin-bottom: ${theme.spacing[6]}px;
  font-size: ${theme.typography.fonts.size[200]}px;
  line-height: ${theme.typography.lineHeights[200]}px;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: ${CONNECTOR_LINE_OFFSET_LEFT};
    top: ${CONNECTOR_LINE_OFFSET_TOP};
    width: ${CONNECTOR_LINE_WIDTH};
    height: ${CONNECTOR_LINE_HEIGHT};
    border-left: ${CONNECTOR_LINE_WIDTH} dotted ${theme.colors.surface.border.gray.muted};
  }
`,
);

// Kept: active-state inner dot needs a ::before pseudo-element.
export const StepRailIcon = styled.span<{ $status: 'active' | 'complete' | 'locked' }>(
  ({ theme, $status }) => `
  position: relative;
  width: ${STEP_ICON_SIZE};
  height: ${STEP_ICON_SIZE};
  flex: 0 0 ${STEP_ICON_SIZE};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  border-radius: 50%;

  svg {
    display: block;
  }
  background: ${
    $status === 'complete'
      ? theme.colors.feedback.background.positive.intense
      : $status === 'active'
      ? theme.colors.interactive.background.primary.faded
      : theme.colors.surface.background.gray.subtle
  };
  color: ${
    $status === 'locked'
      ? theme.colors.surface.text.gray.muted
      : theme.colors.surface.text.staticWhite.normal
  };

  ${
    $status === 'active'
      ? `
        &::before {
          content: '';
          width: ${STEP_ICON_ACTIVE_DOT};
          height: ${STEP_ICON_ACTIVE_DOT};
          border-radius: 50%;
          background: ${theme.colors.interactive.background.primary.default};
        }
      `
      : ''
  }
`,
);

// Kept: custom select trigger button with hover/open border states.
export const FieldTypeButton = styled.button<{ $isOpen: boolean; $hasValue: boolean }>(
  ({ theme, $isOpen, $hasValue }) => `
  width: 100%;
  min-height: ${FIELD_TYPE_BUTTON_MIN_HEIGHT};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]}px;
  border: ${theme.border.width.thin}px solid ${
    $isOpen
      ? theme.colors.interactive.border.primary.default
      : theme.colors.surface.border.gray.muted
  };
  border-radius: ${theme.border.radius.medium}px;
  background: ${theme.colors.surface.background.gray.intense};
  color: ${
    $hasValue ? theme.colors.surface.text.gray.normal : theme.colors.surface.text.gray.muted
  };
  padding: ${theme.spacing[2]}px ${theme.spacing[4]}px;
  text-align: left;
  cursor: pointer;
  font: inherit;
  font-size: ${theme.typography.fonts.size[100]}px;
  line-height: ${theme.typography.lineHeights[100]}px;

  &:hover {
    border-color: ${theme.colors.interactive.border.primary.default};
  }
`,
);

// Kept: listbox overlay needs role/aria attributes which Box does not forward.
export const FieldTypeMenu = styled.div(
  ({ theme }) => `
  position: absolute;
  z-index: 20;
  top: calc(100% + ${theme.spacing[2]}px);
  left: 0;
  right: 0;
  max-height: ${FIELD_TYPE_MENU_MAX_HEIGHT};
  overflow: auto;
  border: ${theme.border.width.thin}px solid ${theme.colors.surface.border.gray.subtle};
  border-radius: ${theme.border.radius.medium}px;
  background: ${theme.colors.surface.background.gray.intense};
  box-shadow: ${theme.elevation.midRaised};
  padding: ${theme.spacing[2]}px;
`,
);

// Kept: option button with hover state and class-targeted helper reveal.
export const FieldTypeOption = styled.button<{ $isSelected: boolean }>(
  ({ theme, $isSelected }) => `
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]}px;
  border: 0;
  border-radius: ${theme.border.radius.medium}px;
  background: ${$isSelected ? theme.colors.interactive.background.primary.faded : 'transparent'};
  color: ${theme.colors.surface.text.gray.normal};
  padding: ${theme.spacing[3]}px;
  text-align: left;
  cursor: pointer;
  font: inherit;
  font-size: ${theme.typography.fonts.size[100]}px;
  line-height: ${theme.typography.lineHeights[100]}px;

  &:hover {
    background: ${theme.colors.interactive.background.primary.faded};
  }

  &:hover .field-type-option-helper,
  &:focus-visible .field-type-option-helper {
    max-height: ${theme.spacing[9]}px;
    opacity: 1;
    margin-top: ${theme.spacing[1]}px;
  }

  ${
    $isSelected
      ? `
        .field-type-option-helper {
          max-height: ${theme.spacing[9]}px;
          opacity: 1;
          margin-top: ${theme.spacing[1]}px;
        }
      `
      : ''
  }
`,
);

// Kept: expand/collapse transition driven by the parent option's hover/selected state.
export const FieldTypeOptionHelper = styled.span(
  ({ theme }) => `
  display: block;
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  color: ${theme.colors.surface.text.gray.muted};
  font-size: ${theme.typography.fonts.size[75]}px;
  line-height: ${theme.typography.lineHeights[50]}px;
  transition: opacity 120ms ease, max-height 120ms ease, margin-top 120ms ease;
`,
);

// Kept: `& + &` sibling selector for the divider between behaviour rows.
export const FieldBehaviorRow = styled.div<{ $isActive: boolean }>(
  ({ theme, $isActive }) => `
  display: grid;
  grid-template-columns: ${FIELD_BEHAVIOR_ICON_COLUMN} minmax(0, 1fr);
  align-items: center;
  gap: ${theme.spacing[4]}px;
  min-height: ${FIELD_BEHAVIOR_ROW_MIN_HEIGHT};
  padding: ${theme.spacing[4]}px ${theme.spacing[5]}px;
  background: ${
    $isActive
      ? theme.colors.interactive.background.primary.faded
      : theme.colors.surface.background.gray.intense
  };

  & + & {
    border-top: ${theme.border.width.thin}px solid ${theme.colors.surface.border.gray.subtle};
  }
`,
);

// Kept: :focus-within border highlight for the chip-input wrapper.
export const EnumValuesField = styled.div(
  ({ theme }) => `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing[2]}px;
  min-height: ${theme.spacing[11]}px;
  border: ${theme.border.width.thin}px solid ${theme.colors.surface.border.gray.muted};
  border-radius: ${theme.border.radius.medium}px;
  padding: ${theme.spacing[2]}px ${theme.spacing[4]}px;
  background: ${theme.colors.surface.background.gray.intense};

  &:focus-within {
    border-color: ${theme.colors.interactive.border.primary.default};
  }
`,
);

// Kept: chip with text sizing on a raw span (chip content is a plain text node).
export const EnumValueChip = styled.span(
  ({ theme }) => `
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]}px;
  border-radius: ${theme.border.radius.max}px;
  background: ${theme.colors.surface.background.gray.moderate};
  color: ${theme.colors.surface.text.gray.normal};
  min-height: ${CHIP_MIN_HEIGHT};
  padding: ${theme.spacing[1]}px ${theme.spacing[3]}px;
  font-size: ${theme.typography.fonts.size[100]}px;
`,
);

// Kept: borderless native text input embedded in the chip field (placeholder styling).
export const EnumValueInput = styled.input(
  ({ theme }) => `
  min-width: ${ENUM_VALUE_INPUT_MIN_WIDTH};
  flex: 1;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${theme.colors.surface.text.gray.normal};
  font: inherit;
  padding: ${theme.spacing[2]}px 0;

  &::placeholder {
    color: ${theme.colors.surface.text.gray.muted};
  }
`,
);

// Kept: unstyled icon-only button (border/background reset + cursor).
export const ChipRemoveButton = styled.button(
  ({ theme }) => `
  border: 0;
  background: transparent;
  color: ${theme.colors.surface.text.gray.muted};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`,
);

// Kept: `& + &` sibling selector for dividers between permission rows.
export const PermissionRow = styled.div(
  ({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${PERMISSION_ACCESS_COLUMN_WIDTH};
  gap: ${theme.spacing[4]}px;
  align-items: center;
  padding: ${theme.spacing[4]}px ${theme.spacing[5]}px;
  position: relative;

  & + & {
    border-top: ${theme.border.width.thin}px solid ${theme.colors.surface.border.gray.subtle};
  }
`,
);

// Kept: custom dropdown trigger with disabled styling and svg child rules.
export const AccessControlButton = styled.button<{ $isDisabled?: boolean }>(
  ({ theme, $isDisabled }) => `
  width: 100%;
  min-height: ${theme.spacing[9]}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[2]}px;
  border: ${theme.border.width.thin}px solid ${theme.colors.surface.border.gray.subtle};
  border-radius: ${theme.border.radius.medium}px;
  background: ${
    $isDisabled
      ? theme.colors.surface.background.gray.subtle
      : theme.colors.surface.background.gray.intense
  };
  color: ${
    $isDisabled ? theme.colors.surface.text.gray.muted : theme.colors.surface.text.gray.normal
  };
  padding: ${theme.spacing[2]}px ${theme.spacing[3]}px;
  cursor: ${$isDisabled ? 'not-allowed' : 'pointer'};
  font: inherit;
  font-size: ${theme.typography.fonts.size[100]}px;
  line-height: ${theme.typography.lineHeights[100]}px;
  text-align: left;

  svg {
    flex: 0 0 auto;
  }
`,
);

// Kept: text ellipsis on the inner span + svg child rules.
export const AccessControlLabel = styled.span(
  ({ theme }) => `
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]}px;
  min-width: 0;
  font-size: ${theme.typography.fonts.size[100]}px;
  line-height: ${theme.typography.lineHeights[100]}px;

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex: 0 0 auto;
  }
`,
);

// Kept: listbox overlay needs role/aria attributes which Box does not forward.
export const AccessMenu = styled.div(
  ({ theme }) => `
  position: absolute;
  z-index: 50;
  top: calc(100% + ${theme.spacing[2]}px);
  left: 0;
  right: 0;
  border: ${theme.border.width.thin}px solid ${theme.colors.surface.border.gray.subtle};
  border-radius: ${theme.border.radius.medium}px;
  background: ${theme.colors.surface.background.gray.intense};
  box-shadow: ${theme.elevation.midRaised};
  padding: ${theme.spacing[2]}px;
`,
);

// Kept: option button with hover state.
export const AccessMenuOption = styled.button<{ $isSelected: boolean }>(
  ({ theme, $isSelected }) => `
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]}px;
  border: 0;
  border-radius: ${theme.border.radius.medium}px;
  background: ${$isSelected ? theme.colors.interactive.background.primary.faded : 'transparent'};
  color: ${theme.colors.surface.text.gray.normal};
  padding: ${theme.spacing[3]}px;
  text-align: left;
  cursor: pointer;
  font: inherit;
  font-size: ${theme.typography.fonts.size[100]}px;
  line-height: ${theme.typography.lineHeights[100]}px;

  &:hover {
    background: ${theme.colors.interactive.background.primary.faded};
  }

  svg {
    flex: 0 0 auto;
  }
`,
);

// Kept: full-width clickable disclosure button (font/cursor reset).
export const NoMemberRolesToggle = styled.button(
  ({ theme }) => `
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: ${theme.border.width.thin}px solid ${theme.colors.surface.border.gray.subtle};
  border-radius: ${theme.border.radius.large}px;
  background: ${theme.colors.surface.background.gray.intense};
  color: ${theme.colors.surface.text.gray.normal};
  padding: ${theme.spacing[4]}px ${theme.spacing[5]}px;
  margin-top: ${theme.spacing[4]}px;
  cursor: pointer;
  font: inherit;
`,
);

// Kept: !important overrides on Blade Table internals. TABLE_HEADER_CELL_HEIGHT
// matches Blade's own header-cell geometry, so it stays a named constant rather
// than a spacing token.
export const EmployeeFieldsTableSurface = styled.div`
  [data-blade-component='table-header-row'] [data-blade-component='table-header-cell'] {
    height: ${TABLE_HEADER_CELL_HEIGHT} !important;
    max-height: ${TABLE_HEADER_CELL_HEIGHT} !important;
  }

  [data-blade-component='table-header-row'] [data-blade-component='table-header-cell'] > div {
    height: ${TABLE_HEADER_CELL_HEIGHT} !important;
    min-height: ${TABLE_HEADER_CELL_HEIGHT} !important;
    max-height: ${TABLE_HEADER_CELL_HEIGHT} !important;
  }
`;

// Kept: expand/collapse width transition with class-targeted opacity/pointer-events
// swaps and a prefers-reduced-motion media query.
export const EmployeeFieldsSearch = styled.div<{ $isExpanded: boolean }>(
  ({ theme, $isExpanded }) => `
  position: absolute;
  top: ${SEARCH_TRIGGER_OFFSET_TOP};
  right: calc(100% + ${SEARCH_TRIGGER_OFFSET_RIGHT});
  width: ${$isExpanded ? SEARCH_EXPANDED_WIDTH : SEARCH_COLLAPSED_WIDTH};
  height: ${theme.spacing[9]}px;
  overflow: hidden;
  transform-origin: right center;
  transition: width 200ms cubic-bezier(0.22, 1, 0.36, 1);

  .collapsed-search-trigger,
  .expanded-search-input {
    position: absolute;
    inset: ${SEARCH_INNER_INSET};
    display: flex;
    align-items: center;
    transition: opacity 160ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .collapsed-search-trigger {
    justify-content: center;
    opacity: ${$isExpanded ? 0 : 1};
    pointer-events: ${$isExpanded ? 'none' : 'auto'};
  }

  .expanded-search-input {
    opacity: ${$isExpanded ? 1 : 0};
    pointer-events: ${$isExpanded ? 'auto' : 'none'};
  }

  .expanded-search-input > * {
    width: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    .collapsed-search-trigger,
    .expanded-search-input {
      transition: none;
    }
  }
`,
);

// Kept: sticky footer with negative horizontal margins (not expressible via Box spacing).
export const FixedFooter = styled.div(
  ({ theme }) => `
  position: sticky;
  bottom: 0;
  margin: ${theme.spacing[2]}px -${theme.spacing[10]}px 0;
  background: ${theme.colors.surface.background.gray.intense};
  border-top: ${theme.border.width.thin}px solid ${theme.colors.surface.border.gray.subtle};
  min-height: ${FIXED_FOOTER_MIN_HEIGHT};
  padding: ${theme.spacing[4]}px ${theme.spacing[10]}px;
  z-index: 2;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-left: 0;
    margin-right: 0;
  }
`,
);

// Kept: targets the FixedFooter component selector to pin it to the flex end.
export const EmployeeFieldsTabLayout = styled.div(
  () => `
  min-height: max(${TAB_LAYOUT_MIN_HEIGHT}, calc(100vh - ${TAB_LAYOUT_VIEWPORT_OFFSET}));
  display: flex;
  flex-direction: column;

  ${FixedFooter} {
    margin-top: auto;
  }
`,
);

// Kept: interactive.background token is not available on Box backgroundColor.
export const ReviewIconTile = styled.div(
  ({ theme }) => `
  width: ${theme.spacing[10]}px;
  height: ${theme.spacing[10]}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.border.radius.medium}px;
  background: ${theme.colors.interactive.background.primary.faded};
`,
);

// Kept: dotted-grid background image; min-width keeps the grid column shrinkable.
export const PreviewPanel = styled.div(
  ({ theme }) => `
  min-width: 0;
  border: ${theme.border.width.thin}px solid ${theme.colors.surface.border.gray.subtle};
  border-radius: ${theme.border.radius.large}px;
  padding: ${theme.spacing[4]}px;
  background-color: ${theme.colors.surface.background.gray.subtle};
  background-image: radial-gradient(${theme.colors.surface.border.gray.muted} ${PREVIEW_DOT_RADIUS}, transparent ${PREVIEW_DOT_RADIUS});
  background-size: ${PREVIEW_DOT_GRID_SIZE} ${PREVIEW_DOT_GRID_SIZE};
`,
);

// Kept: full-width clickable disclosure button (font/cursor reset).
export const ReviewAccessHeader = styled.button(
  ({ theme }) => `
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 0;
  border-top: ${theme.border.width.thin}px solid ${theme.colors.surface.border.gray.subtle};
  background: ${theme.colors.surface.background.gray.intense};
  color: ${theme.colors.surface.text.gray.normal};
  padding: ${theme.spacing[4]}px ${theme.spacing[5]}px;
  cursor: pointer;
  font: inherit;
`,
);
